export interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  roles: string[];
}

export const hasRole = (user: User | null, roles: string[]): boolean => {
  if (!user || !user.roles) return false;
  return user.roles.some(role => roles.includes(role));
};

export const isSuperAdmin = (user: User | null): boolean => {
  return hasRole(user, ['superadmin']);
};

export const isAdmin = (user: User | null): boolean => {
  return hasRole(user, ['superadmin', 'admin']);
};

export const isAgencyOwner = (user: User | null): boolean => {
  return hasRole(user, ['superadmin', 'admin', 'agency_owner']);
};

export const isMarketingSpecialist = (user: User | null): boolean => {
  return hasRole(user, ['superadmin', 'admin', 'agency_owner', 'marketing_specialist']);
};

export const isCompanyOwner = (user: User | null): boolean => {
  return hasRole(user, ['superadmin', 'admin', 'company_owner']);
};

export const isCompanySpecialist = (user: User | null): boolean => {
  return hasRole(user, ['superadmin', 'admin', 'company_owner', 'company_specialist']);
};

export const isPersonal = (user: User | null): boolean => {
  return hasRole(user, ['superadmin', 'admin', 'company_owner', 'personal']);
};

export const canManageCompanies = (user: User | null): boolean => {
  return isAdmin(user) || isAgencyOwner(user);
};

export const canCreateCompanies = (user: User | null): boolean => {
  return isAdmin(user) || isAgencyOwner(user);
};

export const canUpdateCompanies = (user: User | null): boolean => {
  return isAdmin(user) || isAgencyOwner(user);
};

export const canDeleteCompanies = (user: User | null): boolean => {
  return isAdmin(user) || isAgencyOwner(user);
};

export const canManageUsers = (user: User | null): boolean => {
  return isAdmin(user) || isAgencyOwner(user);
};

export const canCreateUsers = (user: User | null): boolean => {
  return isAdmin(user) || isAgencyOwner(user);
};

export const canUpdateUsers = (user: User | null): boolean => {
  return isAdmin(user) || isAgencyOwner(user);
};

export const canDeleteUsers = (user: User | null): boolean => {
  return isAdmin(user);
};

export const canManageAgencyClients = (user: User | null): boolean => {
  return isAdmin(user) || isAgencyOwner(user);
};

export const canAccessBms = (user: User | null): boolean => {
  return hasRole(user, ['superadmin', 'admin', 'agency_owner', 'company_owner', 'company_specialist']);
};

export const isBmsExpertOnly = (user: User | null): boolean => {
  if (!user || !user.roles) return false;
  return user.roles.includes('company_specialist') && !user.roles.some(r =>
    ['superadmin', 'admin', 'agency_owner', 'company_owner'].includes(r)
  );
};

export const getCompanyTypeOptions = (user: User | null): { value: string; label: string }[] => {
  if (isAdmin(user)) {
    return [
      { value: 'agency', label: 'Agency' },
      { value: 'client', label: 'Client' },
      { value: 'single', label: 'Single' }
    ];
  } else if (isAgencyOwner(user)) {
    return [
      { value: 'agency', label: 'Agency' },
      { value: 'client', label: 'Client' },
      { value: 'single', label: 'Single' }
    ];
  }
  return [];
};

export const getRoleOptions = (user: User | null): { value: string; label: string }[] => {
  if (isAdmin(user)) {
    return [
      { value: 'superadmin', label: 'Super Admin' },
      { value: 'admin', label: 'Admin' },
      { value: 'agency_owner', label: 'Agency Owner' },
      { value: 'marketing_specialist', label: 'Marketing Specialist' },
      { value: 'company_owner', label: 'Company Owner' },
      { value: 'company_specialist', label: 'Company Specialist' },
      { value: 'personal', label: 'Personal' }
    ];
  } else if (isAgencyOwner(user)) {
    return [
      { value: 'marketing_specialist', label: 'Marketing Specialist' }
    ];
  } else if (isCompanyOwner(user)) {
    return [
      { value: 'company_specialist', label: 'Company Specialist' },
      { value: 'personal', label: 'Personal' }
    ];
  }
  return [];
}; 